import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  BsGrid, 
  BsClipboardData, 
  BsChevronDown, 
  BsChevronUp 
} from 'react-icons/bs';
import { 
  FiPlusSquare, 
  FiImage, 
  FiLogOut, 
  FiSettings, 
  FiUsers, 
  FiMapPin,
  FiShoppingCart,
  FiTrendingUp,
  FiCreditCard,
  FiUserCheck
} from 'react-icons/fi';
import { MdInventory } from 'react-icons/md';
import { useAuth } from '../contexts/AuthContext';

const Sidebar = () => {
  const { logout, isAdmin, user } = useAuth();
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState({
    products: true,
    business: true,
    analytics: true,
    locations: true,
    settings: true
  });

  const menuSections = [
    {
      id: 'main',
      label: 'Main',
      collapsible: false,
      items: [
        { icon: <BsGrid />, label: 'Dashboard', path: '/', badge: null }
      ]
    },
    {
      id: 'products',
      label: 'Products',
      collapsible: true,
      items: [
        { icon: <FiPlusSquare />, label: 'Add Product', path: '/addnew' },
        { icon: <MdInventory />, label: 'Inventory', path: '/inventory' }
      ]
    },
    {
      id: 'business',
      label: 'Business',
      collapsible: true,
      items: [
        { icon: <FiShoppingCart />, label: 'Auctions', path: '/auctions', badge: 'New' },
        { icon: <FiTrendingUp />, label: 'Bids', path: '/bids' },
        { icon: <FiCreditCard />, label: 'Bank Accounts', path: '/bank-accounts' }
      ]
    },
    {
      id: 'analytics',
      label: 'Analytics & Reports',
      collapsible: true,
      items: [
        { icon: <BsClipboardData />, label: 'Statistics', path: '/statistics' },
        { icon: <FiUsers />, label: 'Customers', path: '/customers' }
      ]
    },
    ...(isAdmin() ? [{
      id: 'locations',
      label: 'Locations',
      collapsible: true,
      items: [
        { icon: <FiMapPin />, label: 'All Branches', path: '/branches' }
      ]
    }] : []),
    ...(isAdmin() ? [{
      id: 'settings',
      label: 'Settings',
      collapsible: true,
      items: [
        { icon: <FiImage />, label: 'Banners', path: '/banners' },
        { icon: <FiUserCheck />, label: 'User Management', path: '/users' },
        { icon: <FiSettings />, label: 'System Settings', path: '/system' }
      ]
    }] : [])
  ];

  const handleLogout = () => {
    logout();
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-gray-900 to-gray-800 text-gray-100">
      {/* Brand Header */}
      <div className="px-4 py-4 border-b border-gray-700 flex-shrink-0">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center shadow-lg">
            <span className="text-xl font-bold text-white">G</span>
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">GoldMart</h1>
            <p className="text-xs text-gray-400">
              {isAdmin() ? 'Admin Dashboard' : 'Branch Manager'}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation - Scrollable */}
      <div className="flex-1 overflow-y-auto py-3 px-3 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-800">
        <nav className="space-y-1">
          {menuSections.map((section) => (
            <div key={section.id} className="mb-1">
              {/* Section Header */}
              {section.collapsible ? (
                <button
                  onClick={() => toggleSection(section.id)}
                  className="flex items-center justify-between w-full px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-200 transition-colors"
                >
                  <span>{section.label}</span>
                  {expandedSections[section.id] ? (
                    <BsChevronUp className="w-3 h-3" />
                  ) : (
                    <BsChevronDown className="w-3 h-3" />
                  )}
                </button>
              ) : (
                <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {section.label}
                </div>
              )}
              
              {/* Section Items */}
              {(!section.collapsible || expandedSections[section.id]) && (
                <div className="mt-1 space-y-0.5">
                  {section.items.map((item, index) => (
                    <Link
                      key={index}
                      to={item.path}
                      className={`flex items-center justify-between px-3 py-2 rounded-lg transition-all group ${
                        isActive(item.path)
                          ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/50'
                          : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <span className={`text-base ${isActive(item.path) ? 'text-white' : 'text-gray-400 group-hover:text-gray-200'}`}>
                          {item.icon}
                        </span>
                        <span className="text-sm font-medium">{item.label}</span>
                      </div>
                      {item.badge && !isActive(item.path) && (
                        <span className="px-2 py-0.5 text-xs font-semibold bg-green-500 text-white rounded-full">
                          {item.badge}
                        </span>
                      )}
                      {isActive(item.path) && (
                        <span className="w-2 h-2 rounded-full bg-white"></span>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
      </div>

      {/* Footer Actions - Fixed at Bottom */}
      <div className="px-3 py-3 border-t border-gray-700 flex-shrink-0">
        <button
          onClick={handleLogout}
          className="flex items-center space-x-3 px-3 py-2.5 w-full rounded-lg text-gray-300 hover:bg-red-600 hover:text-white transition-all group"
        >
          <FiLogOut className="w-5 h-5 group-hover:rotate-12 transition-transform" />
          <span className="text-sm font-medium">Logout</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar; 